import { useMemo, useState, useEffect } from "react";
import { Link, useLocation } from "wouter";
import { ArrowRight, Award, BookOpen, CheckCircle2, ClipboardList, FileCheck, FileText, Folder, GraduationCap, Save, Upload, Video } from "lucide-react";
import { AppLayout } from "@/components/layout/AppLayout";
import { Badge, Card, PrimaryButton, SectionTitle } from "@/components/Card";
import { useDemoStore } from "@/store/useDemoStore";
import { LMS_PREOP_ITEMS_EC1781 } from "@/data/seed";
import { elements as allElements } from "@/data/index";
import {
  useCandidate,
  useCandidatesCrud,
  useCandidatesList,
  useEffectiveUser,
  useEnrollmentsForCandidate,
  useExpedienteDocs,
  useExpedienteForCandidateStandard,
  useExamAttemptsForExpediente,
  useLmsAvancePct,
  useLmsProgress,
  useLmsRoute,
  usePreopRuns,
} from "@/store/hooks";

function useQuery() {
  const [loc] = useLocation();
  return useMemo(() => {
    const s = typeof window !== "undefined" ? window.location.search : "";
    return new URLSearchParams(s);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loc, typeof window !== "undefined" ? window.location.search : ""]);
}

export default function CandidateExpediente() {
  const setRole = useDemoStore((s) => s.setRole);
  useEffect(() => setRole("alumno"), [setRole]);

  const effective = useEffectiveUser();
  const candList = useCandidatesList();
  const fallbackId = candList[0]?.id;
  const candidateId =
    effective && effective.roles.includes("alumno") ? effective.id : fallbackId;
  const me = useCandidate(candidateId);

  const enrollments = useEnrollmentsForCandidate(candidateId);
  const q = useQuery();
  const requested = q.get("codigo");
  const codigo = requested && enrollments.includes(requested) ? requested : enrollments[0];

  const exp = useExpedienteForCandidateStandard(candidateId, codigo);
  const docs = useExpedienteDocs(exp?.id);
  const attempts = useExamAttemptsForExpediente(exp?.id);
  const updateDoc = useDemoStore((s) => s.updateExpedienteDoc);
  const candCrud = useCandidatesCrud();
  const logEvent = useDemoStore((s) => s.logEvent);

  // criterioMap + flat evidencias list — all from static source, bypasses stale localStorage
  const { criterioMap, staticEvidencias } = useMemo(() => {
    const criterioMap: Record<string, { codigo: string; tipo: string }> = {};
    const staticEvidencias: Array<{ id: string; elementCodigo: string; nombre: string; tipo: string; obligatoria: boolean; criterioIds: string[] }> = [];
    for (const el of allElements) {
      for (const c of el.criterios) criterioMap[c.id] = { codigo: c.codigo, tipo: c.tipo };
      for (const ev of el.evidencias) {
        staticEvidencias.push({ id: ev.id, elementCodigo: el.codigo, nombre: ev.nombre, tipo: ev.tipo, obligatoria: ev.obligatoria, criterioIds: ev.criterioIds ?? [] });
      }
    }
    return { criterioMap, staticEvidencias };
  }, []);

  const m = useDemoStore((s) => s.mutations);
  const dictamen = exp ? (m.dictamenIssued[exp.id] ?? exp.dictamen) : "pendiente";
  const certificado = exp ? (m.certificatesIssued.includes(exp.id) || exp.certificado.emitido) : false;

  const [form, setForm] = useState({ telefono: me?.telefono ?? "", email: me?.email ?? "", unidad: me?.unidad ?? "" });
  const [savedFlash, setSavedFlash] = useState(false);
  useEffect(() => {
    setForm({ telefono: me?.telefono ?? "", email: me?.email ?? "", unidad: me?.unidad ?? "" });
  }, [me?.id, me?.telefono, me?.email, me?.unidad]);

  if (!me) {
    return (
      <AppLayout breadcrumb={["Mi expediente"]}>
        <Card><div style={{ padding: 24, color: "var(--cc-gray-600)" }}>No hay candidato activo.</div></Card>
      </AppLayout>
    );
  }
  if (!codigo) {
    return (
      <AppLayout breadcrumb={["Mi expediente"]}>
        <Card><div style={{ padding: 24, color: "var(--cc-gray-600)" }}>No está inscrito en ningún estándar.</div></Card>
      </AppLayout>
    );
  }

  const cargados = docs.filter((d) => d.estado === "cargado").length;
  const observados = docs.filter((d) => d.estado === "observado").length;
  const total = docs.length;

  const onSave = () => {
    candCrud.update(me.id, { telefono: form.telefono, email: form.email, unidad: form.unidad });
    logEvent({ actor: me.nombre, rol: "alumno", accion: "Actualizó datos personales", entidad: "candidato", entidadId: me.id });
    setSavedFlash(true);
    setTimeout(() => setSavedFlash(false), 1800);
  };

  const onUpload = (docId: string) => {
    updateDoc(exp!.id, docId, {
      estado: "cargado",
      subidoEn: new Date().toISOString().slice(0, 10),
      observacion: undefined,
    });
    logEvent({ actor: me.nombre, rol: "alumno", accion: "Cargó evidencia (simulado)", entidad: "documento", entidadId: docId });
  };

  return (
    <AppLayout breadcrumb={["Mi expediente"]}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14, flexWrap: "wrap", gap: 12 }}>
        <div>
          <div style={{ fontSize: 11, color: "var(--cc-gray-500)", fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 4 }}>
            Expediente {exp ? exp.id.toUpperCase() : "—"} · {codigo}
          </div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: "var(--cc-navy)" }}>{me.nombre}</h1>
          <div style={{ fontSize: 13, color: "var(--cc-gray-500)" }}>{me.unidad} · {me.rango} · CURP {me.curp}</div>
        </div>
        <Badge tone={certificado ? "green" : dictamen === "competente" ? "teal" : "amber"}>
          {certificado ? "Certificado emitido" : dictamen === "competente" ? "Competente — pendiente certificado" : "En revisión"}
        </Badge>
      </div>

      {enrollments.length > 1 && (
        <div style={{ display: "flex", gap: 6, marginBottom: 14, flexWrap: "wrap" }}>
          {enrollments.map((c) => (
            <Link key={c} href={`/alumno/expediente?codigo=${c}`}>
              <button
                data-testid={`tab-${c}`}
                style={{
                  padding: "6px 12px", borderRadius: 999, fontSize: 12, fontWeight: 600,
                  border: `1px solid ${c === codigo ? "var(--cc-teal)" : "var(--cc-gray-200)"}`,
                  background: c === codigo ? "var(--cc-teal-bg)" : "white",
                  color: c === codigo ? "var(--cc-teal)" : "var(--cc-gray-700)",
                  cursor: "pointer",
                }}
              >{c}</button>
            </Link>
          ))}
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 16 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <Card>
            <SectionTitle>Datos personales</SectionTitle>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <Field label="Nombre">{me.nombre}</Field>
              <Field label="Rango">{me.rango}</Field>
              <Field label="CURP">{me.curp}</Field>
              <Field label="Matrícula">{me.matricula}</Field>
              <Input label="Teléfono" value={form.telefono} onChange={(v) => setForm({ ...form, telefono: v })} testId="input-telefono" />
              <Input label="Correo" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testId="input-email" />
              <Input label="Unidad" value={form.unidad} onChange={(v) => setForm({ ...form, unidad: v })} testId="input-unidad" />
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 12 }}>
              <PrimaryButton onClick={onSave} testId="button-save-personal">
                <Save size={13} /> Guardar cambios
              </PrimaryButton>
              {savedFlash && <span style={{ fontSize: 12, color: "var(--cc-green)" }}>✓ Guardado</span>}
            </div>
          </Card>

          {exp && <LmsSection expedienteId={exp.id} codigo={codigo} />}

          {exp && (
            <Card>
              <SectionTitle>Componentes del expediente</SectionTitle>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <Row icon={ClipboardList} title="Evaluación diagnóstica" detail={exp.diagnostico.aplicado ? `Aplicada · puntaje ${exp.diagnostico.puntaje} / 100` : "Pendiente"} ok={exp.diagnostico.aplicado} />
                <Row icon={FileCheck} title="Examen teórico" detail={exp.examenTeorico.aplicado ? `Aplicado · puntaje ${m.theoreticalScore ?? exp.examenTeorico.puntaje} / 100 — ${exp.examenTeorico.aprobado ? "aprobado" : "no aprobado"}` : "Pendiente"} ok={exp.examenTeorico.aplicado} />
                <Row icon={Folder} title="Evaluación práctica en circuito" detail={exp.evaluacionPractica.aplicada ? `${exp.evaluacionPractica.criteriosCompetente} de ${exp.evaluacionPractica.criteriosTotales} criterios competentes` : "Pendiente"} ok={exp.evaluacionPractica.aplicada} />
                {certificado && <Row icon={Award} title="Certificado de competencia" detail={`Folio ${exp.certificado.folio ?? "—"} · emitido el ${exp.certificado.fechaEmision ?? "—"}`} ok />}
              </div>
            </Card>
          )}

          {exp && (
            <Card>
              <SectionTitle>Documentos y evidencias</SectionTitle>
              <div style={{ fontSize: 12, color: "var(--cc-gray-500)", marginBottom: 10 }}>
                {cargados} de {total} cargadas · {observados} con observaciones
              </div>
              {docs.length === 0 ? (
                <div style={{ padding: 14, color: "var(--cc-gray-600)", fontSize: 13 }}>
                  Este estándar aún no tiene evidencias configuradas.
                </div>
              ) : (
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                  <thead>
                    <tr style={{ borderBottom: "2px solid var(--cc-gray-100)" }}>
                      <th style={{ textAlign: "left", padding: "6px 8px", fontSize: 11, fontWeight: 600, color: "var(--cc-gray-500)", width: "40%" }}>Evidencia</th>
                      <th style={{ textAlign: "left", padding: "6px 8px", fontSize: 11, fontWeight: 600, color: "var(--cc-gray-500)", width: "30%" }}>Criterios</th>
                      <th style={{ textAlign: "center", padding: "6px 8px", fontSize: 11, fontWeight: 600, color: "var(--cc-gray-500)", width: "15%" }}>Estado</th>
                      <th style={{ textAlign: "center", padding: "6px 8px", fontSize: 11, fontWeight: 600, color: "var(--cc-gray-500)", width: "15%" }}>Acción</th>
                    </tr>
                  </thead>
                  <tbody>
                    {docs.map((d, docIdx) => {
                      const matched = staticEvidencias[docIdx];
                      const displayNombre = matched?.nombre ?? d.nombre;
                      const displayTipo = matched?.tipo ?? d.tipo;
                      const displayObligatoria = matched?.obligatoria ?? d.obligatoria;
                      const icon = displayTipo === "video" ? Video : displayTipo === "foto" ? Folder : FileText;
                      const Icon = icon;
                      const tone = d.estado === "cargado" ? "green" : d.estado === "observado" ? "amber" : "gray";
                      const docCriterioIds = matched?.criterioIds?.length ? matched.criterioIds : (d.criterioIds ?? []);
                      return (
                        <tr key={d.id} data-testid={`doc-${d.id}`} style={{ borderBottom: "1px solid var(--cc-gray-100)" }}>
                          <td style={{ padding: "10px 8px", verticalAlign: "middle" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                              <div style={{ width: 28, height: 28, borderRadius: 6, background: "var(--cc-gray-100)", color: "var(--cc-gray-600)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                                <Icon size={14} />
                              </div>
                              <div>
                                <div style={{ fontWeight: 600, color: "var(--cc-navy)" }}>
                                  {displayNombre} {displayObligatoria && <span style={{ color: "var(--cc-red)", fontSize: 10 }}>*</span>}
                                </div>
                                <div style={{ fontSize: 11, color: "var(--cc-gray-500)" }}>
                                  {displayTipo} · {d.estado === "cargado" ? `Cargada${d.subidoEn ? ` el ${d.subidoEn}` : ""}` : d.estado === "observado" ? (d.observacion ?? "Con observaciones") : "Faltante"}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td style={{ padding: "10px 8px", verticalAlign: "middle" }}>
                            <div style={{ display: "flex", alignItems: "flex-start", gap: 6 }}>
                              {matched?.elementCodigo && (
                                <span style={{ fontSize: 10, fontWeight: 700, color: "var(--cc-gray-400)", whiteSpace: "nowrap", paddingTop: 3, flexShrink: 0 }}>
                                  {matched.elementCodigo}
                                </span>
                              )}
                              <div style={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
                                {docCriterioIds.map((cid) => {
                                  const c = criterioMap[cid];
                                  if (!c) return null;
                                  const chipColor =
                                    c.tipo === "producto" ? "#7c3aed" :
                                    c.tipo === "actitud" ? "#d97706" :
                                    c.tipo === "conocimiento" ? "#16a34a" :
                                    "var(--cc-teal)";
                                  return (
                                    <span key={cid} title={c.tipo} style={{
                                      fontSize: 10, fontWeight: 700, padding: "2px 5px",
                                      borderRadius: 4, background: `${chipColor}15`,
                                      color: chipColor, border: `1px solid ${chipColor}30`,
                                      letterSpacing: "0.02em", whiteSpace: "nowrap",
                                    }}>
                                      {c.codigo}
                                    </span>
                                  );
                                })}
                                {docCriterioIds.length === 0 && <span style={{ fontSize: 11, color: "var(--cc-gray-400)" }}>—</span>}
                              </div>
                            </div>
                          </td>
                          <td style={{ padding: "10px 8px", textAlign: "center", verticalAlign: "middle" }}>
                            <Badge tone={tone as "green" | "amber" | "gray"}>{d.estado.toUpperCase()}</Badge>
                          </td>
                          <td style={{ padding: "10px 8px", textAlign: "center", verticalAlign: "middle" }}>
                            {d.estado !== "cargado" && (
                              <PrimaryButton variant="ghost" onClick={() => onUpload(d.id)} testId={`button-upload-${d.id}`}>
                                <Upload size={12} /> Subir
                              </PrimaryButton>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </Card>
          )}
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <Card>
            <SectionTitle>Resultado del dictamen</SectionTitle>
            {dictamen === "pendiente" ? (
              <div style={{ fontSize: 13, color: "var(--cc-gray-600)", lineHeight: 1.6 }}>
                Su expediente está en revisión por el dictaminador académico.
              </div>
            ) : dictamen === "competente" ? (
              <div style={{ padding: 12, background: "var(--cc-green-bg)", borderRadius: 8 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, color: "var(--cc-green)", fontWeight: 700, fontSize: 13, marginBottom: 4 }}>
                  <CheckCircle2 size={16} /> Competente
                </div>
                <div style={{ fontSize: 12, color: "var(--cc-gray-700)" }}>
                  {certificado ? "Su certificado está disponible." : "El certificado se emitirá en las próximas 24 hrs."}
                </div>
              </div>
            ) : (
              <div style={{ padding: 12, background: "var(--cc-amber-bg)", borderRadius: 8 }}>
                <div style={{ fontWeight: 700, fontSize: 13, color: "var(--cc-amber)" }}>Todavía no competente</div>
              </div>
            )}
          </Card>

          <Card>
            <SectionTitle>Historial</SectionTitle>
            <div style={{ fontSize: 12, color: "var(--cc-gray-600)", marginBottom: 8 }}>
              {attempts.length === 0 ? "Sin intentos registrados todavía." : `${attempts.length} intento(s) registrado(s).`}
            </div>
            <Link href="/alumno/historial">
              <PrimaryButton variant="ghost"><FileText size={13} /> Ver historial completo</PrimaryButton>
            </Link>
          </Card>

          {certificado && exp && (
            <Link href={`/dictaminador/certificado/${exp.id}`} data-testid="link-view-certificate">
              <PrimaryButton><Award size={14} /> Ver mi certificado</PrimaryButton>
            </Link>
          )}
        </div>
      </div>
    </AppLayout>
  );
}

function LmsSection({ expedienteId, codigo }: { expedienteId: string; codigo: string }) {
  const route = useLmsRoute(codigo);
  const progress = useLmsProgress(expedienteId);
  const pct = useLmsAvancePct(expedienteId, codigo);
  const preopRuns = usePreopRuns(expedienteId);
  if (!route) return null;
  const total = route.modules.length;
  const done = progress.completedModuleIds.length;
  const next = route.modules.find((m) => !progress.completedModuleIds.includes(m.id));
  const ready = pct >= 80;
  const completed = route.modules
    .filter((m) => progress.completedModuleIds.includes(m.id))
    .map((m) => {
      const atts = progress.attemptsByModule[m.id] ?? [];
      const last = atts[atts.length - 1];
      return { mod: m, ts: last?.ts, aciertos: last?.aciertos, totalQ: last?.total };
    });
  return (
    <Card testId="lms-section">
      <SectionTitle action={
        <Link href="/alumno/ruta" data-testid="link-open-ruta">
          <PrimaryButton size="sm" variant="outline"><BookOpen size={12} /> Abrir ruta</PrimaryButton>
        </Link>
      }>Formación · {codigo}</SectionTitle>
      <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 10, flexWrap: "wrap" }}>
        <div style={{ width: 44, height: 44, borderRadius: 10, background: ready ? "var(--cc-green-bg)" : "var(--cc-teal-bg)", color: ready ? "var(--cc-green)" : "var(--cc-teal)", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <GraduationCap size={20} />
        </div>
        <div style={{ flex: 1, minWidth: 180 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "var(--cc-navy)" }}>{done} de {total} módulos · {pct}%</div>
          <div style={{ background: "var(--cc-gray-100)", borderRadius: 999, height: 6, marginTop: 6, overflow: "hidden" }}>
            <div style={{ background: ready ? "var(--cc-green)" : "var(--cc-teal)", width: `${pct}%`, height: "100%" }} />
          </div>
        </div>
        {ready ? <Badge tone="green">✓ Listo para evaluación</Badge> : <Badge tone="amber">Falta {80 - pct}%</Badge>}
      </div>
      {next && (
        <div style={{ marginTop: 4, padding: 10, background: "var(--cc-gray-50, #fafbfc)", border: "1px solid var(--cc-gray-100)", borderRadius: 8, display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ fontSize: 12, color: "var(--cc-gray-700)", flex: 1 }}>
            Siguiente: <strong>{next.titulo}</strong>
          </div>
          <Link href={next.kind === "simulador-preop" ? `/alumno/ruta/simulador/${next.id}` : `/alumno/ruta/modulo/${next.id}`} data-testid="link-continue-from-expediente">
            <PrimaryButton size="sm">Continuar <ArrowRight size={11} /></PrimaryButton>
          </Link>
        </div>
      )}
      {completed.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "var(--cc-gray-500)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 6 }}>Módulos cursados</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }} data-testid="lms-modules-done">
            {completed.map(({ mod: m, ts, aciertos, totalQ }) => (
              <div key={m.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 12, padding: "5px 8px", background: "var(--cc-green-bg)", borderRadius: 6 }}>
                <span style={{ color: "var(--cc-gray-800)" }}>✓ {m.titulo}</span>
                <span style={{ color: "var(--cc-gray-600)", fontSize: 11 }}>
                  {ts ? new Date(ts).toLocaleDateString("es-MX") : "—"}
                  {typeof aciertos === "number" && typeof totalQ === "number" && totalQ > 0 ? ` · ${aciertos}/${totalQ}` : ""}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
      {preopRuns.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "var(--cc-gray-500)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 6 }}>Simuladores preoperativos</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }} data-testid="lms-preop-runs">
            {[...preopRuns].reverse().map((r) => {
              const itemLabel: Record<string, { label: string; critical?: boolean }> = {};
              for (const it of LMS_PREOP_ITEMS_EC1781) itemLabel[it.id] = { label: it.label, critical: it.critical };
              return (
                <details key={r.id} style={{ background: r.aprobado ? "var(--cc-green-bg)" : "var(--cc-amber-bg)", borderRadius: 6, padding: "6px 8px" }}>
                  <summary style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 12, cursor: "pointer", color: "var(--cc-gray-800)" }}>
                    <span>
                      {new Date(r.fecha).toLocaleString("es-MX")} · {r.completas}/{r.total}
                      {r.criticasFaltantes > 0 ? ` · ${r.criticasFaltantes} críticas` : ""}
                    </span>
                    <Badge tone={r.aprobado ? "green" : "amber"}>{r.aprobado ? "Aprobado" : "No aprobado"}</Badge>
                  </summary>
                  <div style={{ marginTop: 6, display: "flex", flexDirection: "column", gap: 3, background: "white", padding: 6, borderRadius: 4 }}>
                    {r.entries.map((e) => {
                      const info = itemLabel[e.itemId];
                      const c = e.estado === "cumple" ? { fg: "var(--cc-green)", lbl: "Cumple" }
                        : e.estado === "no-cumple" ? { fg: "var(--cc-red)", lbl: "No cumple" }
                        : { fg: "var(--cc-gray-500)", lbl: "No aplica" };
                      return (
                        <div key={e.itemId} style={{ fontSize: 11, color: "var(--cc-gray-700)", display: "flex", flexDirection: "column", gap: 1 }}>
                          <div style={{ display: "flex", justifyContent: "space-between", gap: 6 }}>
                            <span>
                              {info?.critical && <span style={{ color: "var(--cc-red)", fontWeight: 700, marginRight: 4 }}>●</span>}
                              {info?.label ?? e.itemId}
                            </span>
                            <span style={{ color: c.fg, fontWeight: 700, whiteSpace: "nowrap" }}>{c.lbl}</span>
                          </div>
                          {e.observacion && (
                            <div style={{ fontSize: 10, color: "var(--cc-gray-500)", fontStyle: "italic", paddingLeft: 8 }}>“{e.observacion}”</div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </details>
              );
            })}
          </div>
        </div>
      )}
    </Card>
  );
}

function Row({ icon: Icon, title, detail, ok }: { icon: React.ComponentType<{ size?: number; color?: string }>; title: string; detail: string; ok?: boolean }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, padding: 12, border: "1px solid var(--cc-gray-100)", borderRadius: 10 }}>
      <div style={{ width: 36, height: 36, borderRadius: 10, background: ok ? "var(--cc-green-bg)" : "var(--cc-gray-100)", color: ok ? "var(--cc-green)" : "var(--cc-gray-500)", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Icon size={17} />
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: "var(--cc-navy)" }}>{title}</div>
        <div style={{ fontSize: 12, color: "var(--cc-gray-600)" }}>{detail}</div>
      </div>
      {ok ? <Badge tone="green">OK</Badge> : <Badge tone="amber">PENDIENTE</Badge>}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div style={{ fontSize: 11, color: "var(--cc-gray-500)", fontWeight: 600, marginBottom: 4 }}>{label.toUpperCase()}</div>
      <div style={{ fontSize: 13, color: "var(--cc-navy)" }}>{children}</div>
    </div>
  );
}

function Input({ label, value, onChange, testId }: { label: string; value: string; onChange: (v: string) => void; testId?: string }) {
  return (
    <div>
      <div style={{ fontSize: 11, color: "var(--cc-gray-500)", fontWeight: 600, marginBottom: 4 }}>{label.toUpperCase()}</div>
      <input
        value={value}
        data-testid={testId}
        onChange={(e) => onChange(e.target.value)}
        style={{ width: "100%", padding: "8px 10px", border: "1px solid var(--cc-gray-200)", borderRadius: 8, fontSize: 13 }}
      />
    </div>
  );
}
